import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { CampaignStatus } from '../models/email-campaign.model';

// Below this, the bar just looks like normal in-progress work. Above it, silence starts
// to look indistinguishable from "stuck", so we call that out explicitly.
const STALL_WARNING_SECONDS = 20;

@Component({
  selector: 'app-campaign-status-bar',
  templateUrl: './campaign-status-bar.component.html',
  styleUrls: ['./campaign-status-bar.component.scss']
})
export class CampaignStatusBarComponent implements OnChanges, OnDestroy {

  @Input() status: CampaignStatus;
  // Set when a status poll fails - the bar keeps showing the last known status instead
  // of going blank, plus this banner so it's clear the UI is still trying.
  @Input() pollError: string;
  // Set once polling has given up after too many consecutive failures - at this point
  // there is no more "still trying", so the UI must say so plainly.
  @Input() pollFailed = false;

  private _now = Date.now();
  private _tickSubscription: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.status && this.status && !this.status.is_terminal && !this.pollFailed && !this._tickSubscription) {
      // Ticks once a second purely to refresh the "last updated Xs ago" / stall text below.
      this._tickSubscription = interval(1000).subscribe(() => { this._now = Date.now(); });
    }
    if (this.status?.is_terminal || this.pollFailed) {
      this._tickSubscription?.unsubscribe();
      this._tickSubscription = null;
    }
  }

  ngOnDestroy(): void {
    this._tickSubscription?.unsubscribe();
  }

  get isTerminal(): boolean {
    return !!this.status && this.status.is_terminal;
  }

  // Polling has stopped without the campaign itself ever reaching a terminal state - the
  // status shown is the last one we could confirm, not necessarily the final outcome.
  get isGivenUp(): boolean {
    return !!this.status && !this.status.is_terminal && this.pollFailed;
  }

  get secondsSinceUpdate(): number {
    if (!this.status?.last_updated_at) {
      return 0;
    }
    return Math.max(0, Math.floor((this._now - new Date(this.status.last_updated_at).getTime()) / 1000));
  }

  // Not an error - the campaign is very likely still processing server-side. This just
  // stops "no new counts for a while" from being indistinguishable from "frozen".
  get isStalled(): boolean {
    return !this.isTerminal && this.secondsSinceUpdate >= STALL_WARNING_SECONDS;
  }

  // "accepted" is SMTP handoff, never inbox delivery - keep the labels distinct.
  displayCount(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '—';
    }
    return `${value}`;
  }

  statusLabel(): string {
    if (!this.status) {
      return '';
    }
    switch (this.status.status) {
      case 'draft': return 'Draft';
      case 'prepared': return 'Prepared';
      case 'queued': return 'Queued';
      case 'sending': return 'Sending';
      case 'partially_failed': return 'Partially failed';
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      case 'cancelled': return 'Cancelled';
      default: return this.status.status;
    }
  }
}
